import './index.scss'
import { Form, Input, Button, Checkbox, message } from 'antd'
import { LockOutlined, SafetyCertificateOutlined, ThunderboltOutlined, UserOutlined, ApartmentOutlined } from '@ant-design/icons'
import { useState } from 'react'
import { useDispatch } from 'react-redux'
import { fetchLogin } from '@/store/modules/user'
import { useNavigate } from 'react-router-dom'
import { isFirstLoginUser, markFirstLoginUserSeen, queuePostLoginMenu } from '@/utils/firstLogin'

const USERNAME_MAX_LENGTH = 50
const USERNAME_PATTERN = /^[\u4e00-\u9fa5A-Za-z0-9_]+$/

const LOGIN_CAPABILITY_CARDS = [
  {
    icon: <ApartmentOutlined />,
    title: '多项目与全站库存实时协同',
    desc: '租出、归还、赔偿、租入与跨工地流转全链路台账溯源，不同计量单位严格物理隔离。',
  },
  {
    icon: <ThunderboltOutlined />,
    title: '智能对账试算与五因子归因',
    desc: '一键生成本期财务结算单与物料对账单草稿预览，自动拆解跨期租金变动原因。',
  },
  {
    icon: <SafetyCertificateOutlined />,
    title: '小云 AI 经营参谋全天候护航',
    desc: '内置合同风险审查、接单缺口寻源、老板行动中心风险雷达与逾期应收催缴排行。',
  },
]

const Login = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [form] = Form.useForm()
  const [submitting, setSubmitting] = useState(false)

  const onFinish = async (values) => {
    if (submitting) {
      return
    }
    const normalizedValues = {
      ...values,
      userName: (values?.userName || '').trim()
    }
    setSubmitting(true)
    try {
      const loginResponse = await dispatch(fetchLogin(normalizedValues))
      const userIdentity = {
        cid: loginResponse?.data?.cid,
        userName: normalizedValues.userName
      }

      if (isFirstLoginUser(userIdentity)) {
        markFirstLoginUserSeen(userIdentity)
        queuePostLoginMenu('4-1')
      }

      navigate('/')
      message.success('登录成功')
    } catch (error) {
      message.error(error?.message || '登录失败')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="login-page">
      <div className="login-shell">
        <aside className="login-visual" aria-hidden="true">
          <div className="login-visual-top">
            <div className="visual-brand-row">
              <div className="visual-brand-mark">
                <svg width="24" height="24" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect width="31.5597" height="31.5597" rx="6" fill="#165DFF" />
                  <path d="M24.482 6.99805H8.04283C7.58256 6.99805 7.2085 7.37211 7.2085 7.83238V9.60117C7.2085 10.0614 7.58256 10.4355 8.04283 10.4355H17.5556C17.0745 10.8888 16.6295 11.2754 16.1859 11.6606C16.3194 11.6439 16.4557 11.6356 16.592 11.6369C18.2203 11.6508 19.6067 12.8356 19.9849 14.3958C20.0253 14.5599 20.2269 14.6197 20.3548 14.5098C21.776 13.2903 23.1943 12.0694 24.6141 10.8499C24.9854 10.5315 25.1967 10.0656 25.1953 9.57614V7.74756C25.194 7.33 24.8547 6.99805 24.482 6.99805Z" fill="white" />
                  <path d="M19.4928 20.6631V17.7777C19.4928 17.5719 19.2439 17.4732 19.1007 17.6192C18.4402 18.2978 17.5071 18.7261 16.4892 18.7247C16.1249 18.7066 16.0123 18.8109 16.0123 18.943V20.6576C16.0123 20.7841 15.9094 20.8856 15.7842 20.8856H12.871C13.8847 20.0054 14.6815 19.3282 15.5353 18.5856C14.0975 18.1615 13.0198 16.8155 13.0059 15.2483C13.0393 14.7185 12.8029 14.5906 12.6457 14.7255C11.0883 16.0701 8.16397 18.594 7.46591 19.1961C7.28653 19.35 7.19336 19.52 7.19336 19.7023V23.5332C7.19614 23.9921 7.57159 24.3676 8.03047 24.3676H24.35C24.8103 24.3676 25.1844 23.9935 25.1844 23.5332V21.7269C25.1844 21.2666 24.8103 20.8926 24.35 20.8926H19.7209C19.5943 20.8926 19.4928 20.7897 19.4928 20.6631Z" fill="white" />
                  <circle cx="16.3942" cy="15.2746" r="1.9777" fill="white" />
                </svg>
              </div>
              <span className="visual-brand-title">智建云租</span>
              <span className="visual-brand-tag">AI 驱动 · 业财一体</span>
            </div>

            <h2 className="visual-headline">
              让每一笔工程物资租赁
              <br />
              <span>都算得清、管得住、收得回</span>
            </h2>
            <p className="visual-subheadline">
              融合周转材料全生命周期台账、高精阶梯结算引擎与小云 AI 经营决策参谋，赋能租赁企业高效稳健增长。
            </p>
          </div>

          <div className="login-feature-list">
            {LOGIN_CAPABILITY_CARDS.map((card) => (
              <div className="login-feature-card" key={card.title}>
                <div className="feature-icon-box">{card.icon}</div>
                <div className="feature-body">
                  <div className="feature-title">{card.title}</div>
                  <div className="feature-desc">{card.desc}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="login-visual-footer">
            <div className="trust-pill">100% 真实单据溯源</div>
            <div className="trust-pill">30 秒结算草稿试算</div>
            <div className="trust-pill">多项目全局风控雷达</div>
          </div>
        </aside>

        <div className="login-card">
          <div className="brand-header">
            <div className="brand-badge">
              <svg className="brand-logo-icon" width="28" height="28" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect width="31.5597" height="31.5597" rx="3.50663" fill="#165DFF" />
                <g clipPath="url(#login-brand-logo-clip)">
                  <path d="M24.482 6.99805H8.04283C7.58256 6.99805 7.2085 7.37211 7.2085 7.83238V9.60117C7.2085 10.0614 7.58256 10.4355 8.04283 10.4355H17.5556C17.5362 10.4717 17.5306 10.4911 17.5181 10.5022C17.0745 10.8888 16.6295 11.2754 16.1859 11.6606C16.3194 11.6439 16.4557 11.6356 16.592 11.6369C18.2203 11.6508 19.6067 12.8356 19.9849 14.3958C20.0253 14.5599 20.2269 14.6197 20.3548 14.5098C21.776 13.2903 23.1943 12.0694 24.6141 10.8499C24.9854 10.5315 25.1967 10.0656 25.1953 9.57614C25.194 9.01296 25.194 8.18975 25.194 7.74756C25.194 7.5501 25.1189 7.35959 24.984 7.21497C24.8547 7.07731 24.6725 6.99805 24.482 6.99805Z" fill="white" />
                  <path d="M19.4928 20.6631C19.4928 19.7009 19.4928 18.74 19.4928 17.7777C19.4928 17.5719 19.2439 17.4732 19.1007 17.6192C18.4402 18.2978 17.5071 18.7261 16.4892 18.7247C16.4114 18.7247 16.3335 18.7219 16.257 18.7164C16.1249 18.7066 16.0123 18.8109 16.0123 18.943V20.6576C16.0123 20.7841 15.9094 20.8856 15.7842 20.8856H12.871C12.9586 20.8064 13.024 20.7452 13.0907 20.6882C13.8847 20.0054 14.6815 19.3282 15.4686 18.6385C15.4908 18.619 15.5131 18.6009 15.5353 18.5856C14.0975 18.1615 13.0198 16.8155 13.0059 15.2483C13.0045 15.1398 13.0101 15.0328 13.0198 14.9257C13.0393 14.7185 12.8029 14.5906 12.6457 14.7255C11.0883 16.0701 8.16397 18.594 7.46591 19.1961C7.36579 19.2823 7.28653 19.3908 7.23369 19.5131L7.23229 19.5159C7.20587 19.5757 7.19336 19.6383 7.19336 19.7023C7.19336 20.1625 7.19614 22.151 7.19614 23.2092V23.5332C7.19614 23.9921 7.57159 24.3676 8.03047 24.3676H24.35C24.8103 24.3676 25.1844 23.9935 25.1844 23.5332V21.7269C25.1844 21.2666 24.8103 20.8926 24.35 20.8926H19.7209C19.5943 20.8926 19.4928 20.7897 19.4928 20.6645V20.6631Z" fill="white" />
                  <path d="M16.3942 17.2523C17.4865 17.2523 18.3719 16.3668 18.3719 15.2746C18.3719 14.1823 17.4865 13.2969 16.3942 13.2969C15.3019 13.2969 14.4165 14.1823 14.4165 15.2746C14.4165 16.3668 15.3019 17.2523 16.3942 17.2523Z" fill="white" />
                </g>
                <defs>
                  <clipPath id="login-brand-logo-clip">
                    <rect width="18" height="17.3674" fill="white" transform="translate(7.19531 7)" />
                  </clipPath>
                </defs>
              </svg>
            </div>
            <div className="brand-meta">
              <span className="brand-name">智建云租</span>
              <span className="brand-sub">智能工程物资租赁系统</span>
            </div>
          </div>

          <div className="login-title-block">
            <h1>欢迎登录</h1>
            <p className="login-subtitle">使用您的企业账号登录智建云租数字化管理工作台</p>
          </div>

          <Form
            form={form}
            className="login-form"
            layout="vertical"
            onFinish={onFinish}
            validateTrigger="onBlur"
            initialValues={{ rememberMe: false }}
          >
            <Form.Item
              label="用户名"
              name="userName"
              rules={[
                { required: true, message: '请输入用户名' },
                { max: USERNAME_MAX_LENGTH, message: `用户名最多${USERNAME_MAX_LENGTH}个字符` },
                {
                  validator: (_, value) => {
                    const normalized = (value || '').trim()
                    if (!normalized) {
                      return Promise.resolve()
                    }
                    if (!USERNAME_PATTERN.test(normalized)) {
                      return Promise.reject(new Error('用户名仅支持中文、英文、数字、下划线'))
                    }
                    return Promise.resolve()
                  }
                }
              ]}
            >
              <Input
                prefix={<UserOutlined />}
                size="large"
                placeholder="请输入用户名"
                maxLength={USERNAME_MAX_LENGTH}
                onBlur={(e) => form.setFieldValue('userName', (e?.target?.value || '').trim())}
              />
            </Form.Item>

            <Form.Item
              label="登录密码"
              name="password"
              rules={[{ required: true, message: '请输入密码' }]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                size="large"
                placeholder="请输入密码"
              />
            </Form.Item>

            <div className="login-extra-row">
              <Form.Item name="rememberMe" valuePropName="checked" noStyle>
                <Checkbox className="extra-left">7天内保持登录</Checkbox>
              </Form.Item>
              <button
                className="extra-right extra-link"
                onClick={() => navigate('/forgot-password')}
                type="button"
              >
                忘记密码
              </button>
            </div>

            <Form.Item className="submit-item">
              <Button type="primary" htmlType="submit" size="large" block loading={submitting}>
                登 录
              </Button>
            </Form.Item>

            <div className="register-row">
              还没有企业账户？
              <button
                className="register-link"
                onClick={() => navigate('/register')}
                type="button"
              >
                立即注册
              </button>
            </div>
          </Form>
        </div>
      </div>
    </div>
  )
}

export default Login
